/* =====================================================================
 * @auw/permissions — built-in permission vocabulary.
 *
 * Every permission the SDK checks at render-time is listed here. Adding
 * a new permission means (a) adding the constant below, (b) declaring
 * which tenant roles grant it in the tenant manifest, (c) guarding the
 * UI with `<Permit permission={...}>` or `usePermission(...)`.
 *
 * Namespacing convention
 *   `<scope>.<resource>.<verb>` — e.g. `admin.users.manage`. The
 *   first segment doubles as a rough capability group in future admin
 *   pages that list "what each role can do".
 * ===================================================================== */

import type { Permission } from './types';

/**
 * Admin permissions gate the `/settings/*` management surface plus
 * ancillary admin-only actions (manual feed refresh, billing…).
 */
export const ADMIN_USERS_VIEW: Permission = 'admin.users.view';
export const ADMIN_USERS_MANAGE: Permission = 'admin.users.manage';
export const ADMIN_SKILLS_MANAGE: Permission = 'admin.skills.manage';
export const ADMIN_FEED_REFRESH: Permission = 'admin.feed.refresh';

/** Convenience namespace. Prefer destructuring constants for autocomplete:
 *
 *  ```ts
 *  import { ADMIN_FEED_REFRESH } from '@auw/permissions';
 *  ```
 *
 *  but the bundle is also exported as a single object for tests /
 *  manifest authoring ergonomics. */
export const PERMISSIONS = Object.freeze({
  ADMIN_USERS_VIEW,
  ADMIN_USERS_MANAGE,
  ADMIN_SKILLS_MANAGE,
  ADMIN_FEED_REFRESH,
}) satisfies Readonly<Record<string, Permission>>;

export type BuiltinPermissionId = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
